/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import { t } from 'i18next';
import i18n from '../i18n';
import { ARTICLE_GREEK_MAX_CHARACTERS_LIMIT, ARTICLE_GREEK_OPTIMAL_CHARACTERS_LIMIT, ARTICLE_NON_GREEK_MAX_CHARACTERS_LIMIT, ARTICLE_NON_GREEK_OPTIMAL_CHARACTERS_LIMIT } from './env';
import { isEmptyArray } from '../utils/is/isEmptyArray';

export const KNOWLEDGE_BASE_UPLOAD_STATUSES = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  UPLOADING: 'uploading'
};

export const ARTICLE_GROUP_IDS = {
  UNGROUPED: 'ungrouped',
  NEW: 'new'
};

export const KNOWLEDGE_BASE_ARTICLE_STATUSES = {
  PUBLISHED: 'PUBLISHED',
  UNPUBLISHED: 'UNPUBLISHED'
};

export const ARTICLE_FORM_SIDE_PANEL_BUTTONS = {
  DETAILS: 'DETAILS',
  TRANSLATIONS: 'TRANSLATIONS'
};

export const KNOWLEDGE_BASE_ARTICLE_BODY_CHARACTER_LIMITS = {
  limits: {
    all: {
      optimalCharacterLimit: ARTICLE_NON_GREEK_OPTIMAL_CHARACTERS_LIMIT || 4500,
      maxCharacterLimit: ARTICLE_NON_GREEK_MAX_CHARACTERS_LIMIT || 24576
    },
    el: {
      optimalCharacterLimit: ARTICLE_GREEK_OPTIMAL_CHARACTERS_LIMIT || 1500,
      maxCharacterLimit: ARTICLE_GREEK_MAX_CHARACTERS_LIMIT || 8192
    }
  },
  messages: {
    optimalLimitMessage: t(''),
    warningLimitMessage: t('This size is not ideal. Try reducing the content length.'),
    maxLimitMessage: t('This size exceeds most of the model max content length. Please reduce it.')
  }
};

export const ARTICLE_CHARACTER_LIMIT_TYPES = {
  OPTIMAL: 'optimal',
  WARNING: 'warning',
  MAX: 'max'
};

export const ARTICLE_ID_NEW = 'new';

export const UNGROUPED_ARTICLES_LABEL = i18n.t('Ungrouped Articles');

export const ARTICLE_TITLE_MAX_LENGTH = 1024;

export const ARTICLES_PAGE_SIZE = 5000;

export const KNOWLEDGE_BASE_URL_PARAMS = {
  GROUPS: { sortBy: 'ordinal', order: 'ASC' },
  ARTICLES: { sortBy: 'ordinal', order: 'ASC', page: 0, pageSize: ARTICLES_PAGE_SIZE }
};

export const KNOWLEDGE_BASE_SEARCH_BY_TYPES = {
  ARTICLE: {
    key: 'article',
    filterFunction: (articles, searchTerm, selectedLanguage, primaryLanguage) => {
      if (!searchTerm) {
        return articles;
      }
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      return articles.filter(article => {
        const selectedContent = article.content.find(content => content.languageCode === selectedLanguage);
        const primaryContent = article.content.find(content => content.languageCode === primaryLanguage);
        const title = selectedContent?.title || primaryContent?.title || '';
        return title.toLowerCase().includes(lowerCaseSearchTerm);
      });
    },
    placeHolderText: t('Search by Article Name')
  },
  GROUP: {
    key: 'group',
    filterFunction: (articles, searchTerm, selectedLanguage, primaryLanguage, groups) => {
      if (!searchTerm) {
        return groups;
      }

      if(!groups || isEmptyArray(groups)) {
        return [];
      }

      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const matchingGroup = groups
        .filter(group => {
          const groupName = group.name?.[selectedLanguage] || group.name?.[primaryLanguage] || '';
          return groupName.toLowerCase().includes(lowerCaseSearchTerm);
        });
      return matchingGroup;
    },
    placeHolderText: t('Search by Group Name')
  },
  TAG: {
    key: 'tag',
    filterFunction: (articles, searchTerm, selectedLanguage) => {
      if (!searchTerm) {
        return articles;
      }
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      return articles.filter(article => {
        const selectedContent = article.content.find(content => content.languageCode === selectedLanguage);
        return selectedContent?.tags?.some(tag => tag.toLowerCase().includes(lowerCaseSearchTerm));
      });
    },
    placeHolderText: t('Search by Tag')
  },
  ARTICLE_BODY: {
    key: 'articleBody',
    filterFunction: (articles, searchTerm, selectedLanguage) => {
      if (!searchTerm) {
        return articles;
      }
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      return articles.filter(article => {
        const selectedContent = article.content.find(content => content.languageCode === selectedLanguage);
        const body = selectedContent?.body || '';
        return body.toLowerCase().includes(lowerCaseSearchTerm);
      });
    },
    placeHolderText: t('Search in Article Body')
  }
};

export const KNOWLEDGE_BASE_GROUP_ACTIONS = {
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  CREATE: 'CREATE',
  CANCEL: 'CANCEL'
};
